// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ReactComponent as CopyIcon } from "../assets/icons/copy-icon.svg";
import { ReactComponent as NoGroupsIcon } from "../assets/icons/no-groups-icon.svg";
import { ReactComponent as RightArrow } from "../assets/icons/right-arrow.svg";
import { ReactComponent as GroupIcon } from "../assets/icons/group-icon.svg";
import { setModalState, modalViewsConst } from "../store/modalSlice";
import { useSelector } from "react-redux";
import { fetchAssetsByGroupId } from "../store/assetsSlice";
import { getUrl } from '@aws-amplify/storage';
import { imageCache } from '../utils/imageCache';
import { del } from 'aws-amplify/api';
import bgpattern from "../assets/images/bg-pattern-01.svg"
import blankOrganization from "../assets/images/blank_organization.png"
import { RootState } from "../store";
import ProfilePictureRounded from "./ProfilePictureRounded";
import { Skeleton, SkeletonText } from "./Skeleton";
import { usePostHogCapture } from "../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../utils/posthogEvents";

export const AssetGroupFolder = ({ assetGroup }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { capture } = usePostHogCapture();
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const { assets, user } = useSelector((state: RootState) => ({
    assets: state.assets,
    user: state.user
  }));
  
  // Get current user ID from user details
  const currentUserId = user?.permissionDetails?.id;

  const assetCount = assets?.dataGroups?.[assetGroup.id]?.length ?? 0;
  
  // Check if the asset group should be editable: - Either the user is an Admin OR - The user is the owner of the asset group
  const canEditAssetGroup = user?.details?.groups?.includes("Admin") || 
                           (currentUserId && assetGroup.owner === currentUserId);
                         
  // Determine if the asset group is organization-owned and not editable
  const isOrganizationOwned = assetGroup?.organizationId && !canEditAssetGroup;

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (assetGroup.profilePictureUrl) {
        try {
          const cachedUrl = await imageCache.getSignedUrl(assetGroup.profilePictureUrl);
          if (cachedUrl) {
            setBackgroundUrl(cachedUrl);
          }
        } catch (error) {
          console.error('Error fetching profile picture URL:', error);
        }
      }
    };

    fetchBackgroundImage();
  }, [assetGroup.profilePictureUrl]);

  useEffect(() => {
    // error handling here
    if (!assets.dataGroups[assetGroup.id]) {
      dispatch(fetchAssetsByGroupId(assetGroup.id));
    }
  }, []);

  const handleOuterDiv = (e) => {
    capture(POSTHOG_EVENTS.ASSET_GROUP_SELECTED, {
      asset_group_id: assetGroup.id,
      asset_group_name: assetGroup.name,
    });
    navigate(`/home/group/${assetGroup.id}`);
  };

  const handleEdit = (e) => {
    // Skip if the user can't edit this asset group
    if (isOrganizationOwned) return;
    
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.editAssetGroup,
        properties: assetGroup,
      })
    );

    e.stopPropagation();
  };


  return (
    <>
      {/* Mirrors the website's solutions mega-menu card: image rail left, copy right */}
      <div
        onClick={handleOuterDiv}
        data-testid="asset-group-card"
        className="eai-rail-card group cursor-pointer relative w-full min-w-0"
      >
        <div
          className="eai-rail bg-eai-surface bg-center"
          style={{
            backgroundImage: backgroundUrl
              ? `url(${backgroundUrl})`
              : `url(${bgpattern})`, // Default background image
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
        >
          {/* Without a group picture the rail is just the pattern — badge it so the card still reads as a group */}
          {!backgroundUrl && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-eai-border bg-white shadow-eai-subtle">
                <GroupIcon className="h-6 w-6" />
              </div>
            </div>
          )}
        </div>

        <div className="eai-rail-copy">
          <div className="flex items-start gap-2">
            <span className="min-w-0 flex-1 line-clamp-2 break-words font-outfit text-xl font-normal leading-[1.2] tracking-[0.02em] text-eai-ink transition-all group-hover:font-semibold">
              {assetGroup.name}
            </span>
            <span className="shrink-0 text-eai-body-sm text-eai-ink">→</span>
          </div>

          <div className="mt-auto flex items-end justify-between">
            {/* Count rides the bottom row in the shared count-pill style */}
            <div className="flex items-center gap-2">
              {assetGroup?.organizationId &&
                (user?.organizations?.loaded && user?.organizations?.data?.[assetGroup.organizationId] ? (
                  <ProfilePictureRounded
                    pictureURL={user.organizations.data[assetGroup.organizationId].profilePictureUrl}
                    diameter={28}
                    className="border border-eai-border shadow-eai-subtle"
                  />
                ) : (
                  <div style={{ width: "28px", height: "28px" }} className="rounded-full border border-eai-border shadow-eai-subtle overflow-hidden">
                    <img src={blankOrganization} alt="Organization" className="w-full h-full object-cover" />
                  </div>
                ))}
              <span className="eai-count-pill">
                {assetCount} {assetCount === 1 ? "Asset" : "Assets"}
              </span>
            </div>

            {/* Actions on the card — stop the clicks reaching the card's navigation. Delete lives in the edit modal now, behind its confirm step. */}
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={handleEdit}
                disabled={isOrganizationOwned}
                aria-label={`Edit ${assetGroup.name}`}
                title={
                  isOrganizationOwned
                    ? "Only group owners can edit organization-owned groups"
                    : "Edit group"
                }
                className="eai-btn-outline-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Placeholder matching AssetGroupFolder's layout — keep the two in sync when the card changes.
export const AssetGroupFolderSkeleton = () => (
  <div className="eai-rail-card h-full w-full min-w-0">
    <Skeleton className="eai-rail" radius="none" />
    <div className="eai-rail-copy">
      <SkeletonText lines={2} lineClassName="h-3.5" />
      <div className="mt-auto flex items-end justify-between">
        {/* Bottom row: avatar + count pill left, edit right */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-7" radius="full" />
          <Skeleton className="h-5 w-16" radius="full" />
        </div>
        <Skeleton className="h-eai-control-xs w-12" radius="full" />
      </div>
    </div>
  </div>
);

export const NoAssetGroup = ({ handleAddAssetGroup }) => {
  return (
    <>
      <div className="min-w-[400px] rounded-eai-card border border-eai-border flex flex-col grow py-8 justify-center items-center">
        <NoGroupsIcon />
        <div className="mb-2 flex items-center gap-2">
          <span className="eai-slash leading-none">//</span>
          <span className="eai-eyebrow">Getting started</span>
        </div>
        <div className="eai-h3-title">
          No Asset Groups
        </div>
        <div className="w-[245px] text-center eai-description-sm mb-10">
          Create an asset group to begin creating asset profiles.
        </div>

        <div
          onClick={() => handleAddAssetGroup()}
          className="eai-card-interactive cursor-pointer flex flex-row justify-center items-center p-3"
        >
          <div className="w-12 h-12 shrink-0 flex items-center justify-center mr-3 bg-white rounded-full border border-eai-border">
            <GroupIcon className="w-6 h-6" />
          </div>
          <div className="flex flex-col grow justify-center">
            <div className="font-outfit text-eai-body font-semibold text-eai-ink mb-1">
              Create an Asset Group
            </div>
            <div className="eai-description-sm">
              Before you add an asset, create your first group.{" "}
            </div>
          </div>
          <div className="w-12 h-12 shrink-0 flex items-center justify-center ml-3">
            <RightArrow className="w-6 h-6" />
          </div>
        </div>
      </div>
    </>
  );
};
